import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { FOCUS_RING } from "~/lib/utils";

/**
 * Keeps one screen's failure inside that screen.
 *
 * Without this, an exception anywhere in a route unmounts the entire React tree
 * — sidebar included — and the operator is left on a blank white page with no
 * way back and nothing to report. That is exactly what happened the first time
 * a container handed a screen an unparseable date: `date-fns` threw, and the
 * whole application went from twenty-six navigation links to zero.
 *
 * A blank page is also the worst possible diagnostic. It looks identical
 * whether the app failed to load, the session expired, or one table cell got a
 * bad value, so the report that reaches an engineer is "it broke" and the first
 * hour goes on reproducing it. Naming the route and showing the message turns
 * that into a one-line report.
 *
 * Keyed at the call site: a stable `"mail"` key for the mail workspace so
 * thread clicks do not remount it, and pathname for everything else so
 * navigating away from a broken screen resets the failure.
 */
interface Props {
  /** Shown so a report names the screen rather than the whole product. */
  routeTitle: string;
  onGoBack: () => void;
  children: ReactNode;
}

interface State {
  error?: Error;
}

export class RouteBoundary extends Component<Props, State> {
  override state: State = {};

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The stack is the thing an engineer actually needs, and React swallows it
    // once the boundary renders.
    console.error(`[route] ${this.props.routeTitle} failed to render`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Card
        role="alert"
        className="mx-auto w-full min-w-0 max-w-xl gap-0 rounded-lg bg-card py-0 shadow-sm"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-error-ink" />
          <div className="min-w-0">
            <h1 className="text-[13px] font-semibold leading-snug tracking-tight text-foreground">
              {this.props.routeTitle} could not be displayed
            </h1>
            <p className="mt-0.5 max-w-prose text-[13px] leading-snug break-words text-foreground-muted [overflow-wrap:anywhere]">
              Something in this screen failed while rendering. The rest of InboxFury is
              unaffected — the navigation on the left still works.
            </p>
            <p className="mt-2 break-words font-mono text-xs text-foreground-subtle [overflow-wrap:anywhere]">
              {error.message}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2.5">
          <Button
            type="button"
            variant="primary"
            size="sm"
            className={FOCUS_RING}
            onClick={() => this.setState({ error: undefined })}
          >
            <RotateCw aria-hidden="true" className="size-3.5 shrink-0" />
            Try again
          </Button>
          <Button type="button" variant="outline" size="sm" className={FOCUS_RING} onClick={this.props.onGoBack}>
            Go back
          </Button>
        </div>
      </Card>
    );
  }
}
